<div class="invoice-page">

	<!-- ── Header ─────────────────────────────────────────────────── -->
	<div class="page-header">
		<div class="page-title">
			<h1>Approved invoices</h1>
			<p class="subtitle">Summary of all approved ticket invoices</p>
		</div>
	</div>

	<!-- ── Filter bar ─────────────────────────────────────────────── -->
	<form [formGroup]="filterForm" (ngSubmit)="onApplyFilter()" class="filter-bar">
		<div class="filter-group">
			<label for="fromDate">From</label>
			<input id="fromDate" type="date" formControlName="fromDate"
				[value]="filterForm.get('fromDate')?.value | date:'yyyy-MM-dd'" />
		</div>
		<div class="filter-group">
			<label for="toDate">To</label>
			<input id="toDate" type="date" formControlName="toDate"
				[value]="filterForm.get('toDate')?.value | date:'yyyy-MM-dd'" />
		</div>
		<button type="submit" class="btn-apply" [disabled]="isLoading || filterForm.invalid">
			<span *ngIf="!isLoading">Apply</span>
			<span *ngIf="isLoading" class="spinner" aria-label="Loading"></span>
		</button>
	</form>

	<!-- ── Summary cards ───────────────────────────────────────────── -->
	<div class="summary-strip" *ngIf="!isLoading && invoices.length > 0">
		<div class="summary-card">
			<span class="summary-label">Invoices</span>
			<span class="summary-value">{{ invoiceCount }}</span>
		</div>
		<div class="summary-card">
			<span class="summary-label">Grand total</span>
			<span class="summary-value grand">{{ formatCurrency(grandTotal()) }}</span>
		</div>
	</div>

	<!-- ── Error ───────────────────────────────────────────────────── -->
	<div class="error-banner" *ngIf="errorMessage" role="alert">
		<i class="ti ti-alert-circle"></i>
		{{ errorMessage }}
	</div>

	<!-- ── Loading skeleton ────────────────────────────────────────── -->
	<div class="skeleton-rows" *ngIf="isLoading" aria-label="Loading invoices">
		<div class="skeleton-row" *ngFor="let i of [1,2,3,4,5]"></div>
	</div>

	<!-- ── Empty state ─────────────────────────────────────────────── -->
	<div class="empty-state" *ngIf="!isLoading && !errorMessage && invoices.length === 0">
		<i class="ti ti-file-off"></i>
		<p>No approved invoices found for the selected period.</p>
	</div>

	<!-- ── Invoice table ───────────────────────────────────────────── -->
	<div class="table-wrap" *ngIf="!isLoading && invoices.length > 0">
		<table class="invoice-table">
			<thead>
				<tr>
					<th class="col-expand"></th>
					<th class="col-id">Invoice #</th>
					<th class="col-date">Approved</th>
					<th class="col-recipient">Recipient</th>
					<th class="col-workflow">Workflow</th>
					<th class="col-lines">Lines</th>
					<th class="col-total">Total</th>
				</tr>
			</thead>
			<tbody>
				<ng-container *ngFor="let invoice of invoices">

					<!-- Main row -->
					<tr class="invoice-row" [class.expanded]="isExpanded(invoice.id)" (click)="toggleRow(invoice.id)"
						[attr.aria-expanded]="isExpanded(invoice.id)">
						<td class="col-expand">
							<i class="ti" [class.ti-chevron-right]="!isExpanded(invoice.id)"
								[class.ti-chevron-down]="isExpanded(invoice.id)" aria-hidden="true"></i>
						</td>
						<td class="col-id">
							<span class="invoice-id">#{{ invoice.id }}</span>
						</td>
						<td class="col-date">{{ formatDate(invoice.paymentAt) }}</td>
						<td class="col-recipient">{{ recipientLabel(invoice) }}</td>
						<td class="col-workflow">{{ invoice.workflow?.name ?? '—' }}</td>
						<td class="col-lines">
							<span class="lines-badge">{{ invoice.lines?.length ?? 0 }}</span>
						</td>
						<td class="col-total">{{ formatCurrency(invoiceTotal(invoice)) }}</td>
					</tr>

					<!-- Expanded sub-table row -->
					<tr class="detail-row" [class.visible]="isExpanded(invoice.id)" *ngIf="isExpanded(invoice.id)">
						<td colspan="8" class="detail-cell">
							<div class="detail-panel">
								<table class="lines-table" *ngIf="invoice.lines?.length; else noLines">
									<thead>
										<tr>
											<th>Name</th>
											<th>Description</th>
											<th class="num">Unit cost</th>
											<th class="num">Qty</th>
											<th class="num">Line total</th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let line of invoice.lines">
											<td>{{ line.name }}</td>
											<td class="description">{{ line.description || '—' }}</td>
											<td class="num">{{ formatCurrency(line.cost) }}</td>
											<td class="num">{{ line.amount }}</td>
											<td class="num line-total">{{ formatCurrency(lineTotal(line)) }}</td>
										</tr>
									</tbody>
									<tfoot>
										<tr>
											<td colspan="4" class="subtotal-label">Invoice total</td>
											<td class="num subtotal-value">{{ formatCurrency(invoiceTotal(invoice)) }}
											</td>
										</tr>
									</tfoot>
								</table>
								<ng-template #noLines>
									<p class="no-lines">No line items recorded for this invoice.</p>
								</ng-template>
							</div>
						</td>
					</tr>

				</ng-container>
			</tbody>

			<!-- Grand total footer -->
			<tfoot>
				<tr class="grand-total-row">
					<td colspan="6" class="grand-label">
						{{ invoiceCount }} invoice{{ invoiceCount !== 1 ? 's' : '' }}
					</td>
					<td class="col-lines grand-count">
						<span class="lines-badge">
							{{ getInvoiceCount(invoices)}}
						</span>
					</td>
					<td class="col-total grand-value">{{ formatCurrency(grandTotal()) }}</td>
				</tr>
			</tfoot>
		</table>
	</div>

</div>
